import { type ComputedRef, type Ref, unref } from 'vue'
import { useRoute } from 'vue-router'

import {
  MAKLER_ASSISTANT_FIELD_KEYS,
  MAKLER_ENTER_DATA_FIELD_ORDER,
  MAKLER_REVIEW_ONLY_FIELD_KEYS,
  maklerAssistantFieldKeys,
} from '@/lib/ai/maklerOnboardingFields'
import { getDataEntryFieldLabelKey, getFirstMissingAssistantField } from '@/lib/ai/nextFieldPrompt'
import { hasMeaningfulOnboardingValue } from '@/lib/ai/onboardingFieldValues'
import { useAiAssistantStore } from '@/stores/aiAssistant'
import type { AiUiContext, AiUiCrmContext } from '@/stores/aiAssistant/types'

/**
 * Maps onboarding step route slugs to the dot-paths of fields visible on that step.
 * Sourced from onboarding-schema.json x-step definitions.
 * Only unconditionally-visible fields are listed — feature-flagged fields can be added later.
 */
const STEP_VISIBLE_FIELDS: Record<string, string[]> = {
  // New wizard steps
  welcome: [],
  'basic-info': ['name', 'website'],
  'enter-data': [...MAKLER_ENTER_DATA_FIELD_ORDER],
  /** Assistant only prompts for enter-data fields (excludes review-only legal URLs). */
  'enter-data-assistant': [...MAKLER_ASSISTANT_FIELD_KEYS],
  payment: [
    'firstname',
    'lastname',
    'phone',
    'email',
    'companyName',
    'vatId',
    'taxNumber',
    'paymentMethod',
    'iban',
    'bic',
  ],
  review: [...MAKLER_ENTER_DATA_FIELD_ORDER, ...MAKLER_REVIEW_ONLY_FIELD_KEYS],
  // Legacy steps (kept for backward compatibility with backend agents).
  // Contact person (GENERAL) and public-facing ambassador are separate enter-data sections.
  company: ['name', 'street', 'zip', 'city', 'country', 'website', 'imprint', 'privacy', 'about'],
  publication: [
    'theme.colors.primary',
    'theme.colors.secondary',
    'ambassador.firstname',
    'ambassador.lastname',
    'ambassador.role',
    'ambassador.email',
    'ambassador.phone',
    'ambassador.mobile',
    'slogan',
    'businessDescription',
  ],
  guide: ['selectedLandingpages'],
  'email-validation': ['emailValidation'],
  validation: ['acceptPrivacy', 'acceptOnboarding'],
}

/**
 * English step titles used in the OpenAI system prompt context block.
 * Kept in English intentionally — the "respond in {locale}" instruction handles response language.
 */
const STEP_TITLES: Record<string, string> = {
  // New wizard steps
  welcome: 'Welcome',
  'basic-info': 'Basic Info — Company Name & Website',
  'enter-data': 'Enter Data — Company Information, Brand Colors & Social Media',
  payment: 'Billing Data',
  guide: 'Guide Selection (Ratgeber)',
  review: 'Review & Submit',
  // Legacy steps (contact person and ambassador are separate on enter-data / review)
  company: 'Company Data',
  publication: 'Publication / Brand Data',
  'email-validation': 'E-Mail Sender Validation',
  validation: 'Final Agreement & Submission',
}

/**
 * Safely resolves a dot-path string against a nested object.
 * e.g. getByPath({ theme: { colors: { primary: '#fff' } } }, 'theme.colors.primary') → '#fff'
 */
function getByPath(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc !== null && acc !== undefined && typeof acc === 'object') {
      return (acc as Record<string, unknown>)[key]
    }
    return undefined
  }, obj)
}

/** First visible field on the step (in schema order) with no value — for AI nudges. */
export function getFirstMissingVisibleField(
  stepSlug: string,
  data: Record<string, unknown>,
  options: { isUsOnboarding?: boolean } = {}
): string | null {
  const visible =
    stepSlug === 'enter-data-assistant'
      ? [...maklerAssistantFieldKeys(Boolean(options.isUsOnboarding))]
      : (STEP_VISIBLE_FIELDS[stepSlug] ?? [])
  for (const field of visible) {
    if (!hasMeaningfulOnboardingValue(field, getByPath(data, field))) return field
  }
  return null
}

export { getDataEntryFieldLabelKey, getFirstMissingAssistantField }

interface UseOnboardingAiContextOptions {
  /** Reactive ref holding the active step route slug, e.g. 'publication' */
  activeStepSlug: Ref<string>
  /** Reactive ref holding the 1-based step index */
  activeStepIndex: Ref<number>
  /**
   * Reactive ref holding the current step's data as a nested object.
   * The composable resolves dot-path fields from this using getByPath().
   *
   * Examples per step:
   *   company:     { name, street, zip, city, country, website, imprint, privacy, about }
   *   publication: { theme: { colors: { primary, secondary } }, ambassador: {...}, slogan }
   *   contact:     { contactPerson: { firstname, lastname, phone, email } }
   */
  currentStepData: Ref<Record<string, unknown>>
  /** Optional CRM snapshot from App `provide('crmConfigData')`. */
  crmContext?: Ref<AiUiCrmContext | null> | ComputedRef<AiUiCrmContext | null>
  /** Makler enter-data layout: chat (assisted Step 2) | form (manual fallback). */
  enterDataMode?: Ref<'chat' | 'form'> | ComputedRef<'chat' | 'form'>
  /** When true, US-only enter-data keys are included in assistant visible fields. */
  isUsOnboarding?: Ref<boolean> | ComputedRef<boolean>
  /** When false, session create/bootstrap must not trigger the Genaisis website scrape. */
  enableWebsiteScrape?: Ref<boolean> | ComputedRef<boolean>
}

export function useOnboardingAiContext({
  activeStepSlug,
  activeStepIndex,
  currentStepData,
  crmContext,
  enterDataMode,
  isUsOnboarding,
  enableWebsiteScrape,
}: UseOnboardingAiContextOptions) {
  const aiStore = useAiAssistantStore()
  const route = useRoute()

  const buildContext = (): AiUiContext => {
    const slug = activeStepSlug.value
    const usMarket = isUsOnboarding != null ? Boolean(unref(isUsOnboarding)) : false
    const visibleFields =
      slug === 'enter-data-assistant'
        ? [...maklerAssistantFieldKeys(usMarket)]
        : (STEP_VISIBLE_FIELDS[slug] ?? [])
    const currentValues: Record<string, unknown> = {}
    const crm = crmContext != null ? unref(crmContext) : null

    for (const field of visibleFields) {
      currentValues[field] = getByPath(currentStepData.value, field)
    }

    // companyId isn't a visible form field, so the loop above drops it — but chat
    // widgets (e.g. the image-upload tile) gate on it. Surface it explicitly so the
    // upload button isn't permanently disabled.
    const companyId = getByPath(currentStepData.value, 'companyId')
    if (companyId !== undefined) currentValues.companyId = companyId

    const nextMissingField =
      slug === 'enter-data'
        ? getFirstMissingAssistantField(currentStepData.value, { isUsOnboarding: usMarket })
        : null

    return {
      currentRoute: String(route.name ?? route.path),
      activeStepId: slug,
      stepIndex: activeStepIndex.value,
      stepTitle: STEP_TITLES[slug] ?? slug,
      visibleFields,
      currentValues,
      nextMissingField,
      ...(isUsOnboarding != null ? { isUsOnboarding: usMarket } : {}),
      ...(enterDataMode != null ? { enterDataMode: unref(enterDataMode) } : {}),
      ...(crmContext != null ? { crm } : {}),
      ...(enableWebsiteScrape != null
        ? { enableWebsiteScrape: Boolean(unref(enableWebsiteScrape)) }
        : {}),
    }
  }

  /**
   * Pushes the latest context snapshot to the AI store.
   * Call this whenever the active step or the underlying form data changes.
   */
  const syncContext = (): void => {
    aiStore.setUiContext(buildContext())
  }

  /**
   * Compatible with the AiAssistantDrawer `contextLoader` prop.
   * Also writes to the store so the context is available before the first message.
   */
  const contextLoader = (): string => {
    const ctx = buildContext()
    aiStore.setUiContext(ctx)
    return JSON.stringify(ctx, null, 2)
  }

  return { contextLoader, syncContext, buildContext }
}
